import React from 'react';
import { formatCurrency } from '../utils/formatCurrency';

/**
 * Manual spend-adjustment marker (2026-08-15) — THE indicator that a displayed
 * spend-to-date figure includes an owner-ruled manual adjustment.
 *
 * One definition, two surfaces (BudgetCostCodes' Spent to Date cells,
 * BudgetOverview's Cost to Date cells) — extracted up front rather than
 * duplicated, per the Code Unification Principle.
 *
 * The tooltip is RULED wording: "Computed £X + manual adjustment £Y", with a
 * true minus sign for a negative delta — and DELIBERATELY no who/date (owner:
 * "there may be quite a few minor adjustmens over the film"; who/when/old/new
 * live in the durable budget_audit_log server-side, not in the UI).
 *
 * Colour follows the page's own precedent for a value-with-caveat marker (the
 * weekly-rate override asterisk in BudgetCostCodes): amber-600/amber-400 —
 * measured shades; amber-500 fails WCAG 1.4.11 on white. The marker is NOT a
 * sole carrier: the adjusted NUMBER is right beside it and the tooltip +
 * aria-label carry the explanation, so colour is never the only channel.
 *
 * Zero renders NOTHING — 0 is "no adjustment" by ruling (the displayed spend
 * is identical either way), so callers may render unconditionally.
 */
export function spendAdjustmentTooltip(computed, adjustment) {
  const adj = Number(adjustment) || 0;
  const sign = adj < 0 ? '−' : '+';
  return `Computed ${formatCurrency(Number(computed) || 0)} ${sign} manual adjustment ${formatCurrency(Math.abs(adj))}`;
}

export default function SpendAdjustmentMarker({ computed, adjustment }) {
  const adj = Number(adjustment) || 0;
  if (adj === 0) return null;
  const tip = spendAdjustmentTooltip(computed, adj);
  return (
    <span
      className="text-amber-600 dark:text-amber-400 text-xs ml-0.5 cursor-help"
      title={tip}
      role="img"
      aria-label={tip}
      data-testid="spend-adjustment-marker"
    >
      *
    </span>
  );
}
